var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import ReactFlow, { MiniMap, Controls, ReactFlowProvider } from "reactflow";
import "reactflow/dist/base.css";
import { layoutTree } from "./tree-layout";
import { allChildren, visitTreeNodes } from "../tree-description";
import { useMemo, useEffect, useRef } from "react";
import { QueryNode } from "./QueryNode";
import { useGraphRenderingStore } from "./store";
import "./QueryGraph.css";
function minimapNodeColor(n) {
    if (n.data.nodeColor)
        return n.data.nodeColor;
    if (n.data.iconColor)
        return n.data.iconColor;
    return "hsl(0, 0%, 72%)";
}
var nodeTypes = {
    querynode: QueryNode,
};
function QueryGraphInternal(_a) {
    var treeDescription = _a.treeDescription, children = _a.children;
    // Assign ids to all nodes
    var nodeIdMapping = useMemo(function () {
        var nextId = 0;
        var nodeIds = new Map();
        visitTreeNodes(treeDescription.root, function (d) {
            nodeIds.set(d, "" + nextId++);
        }, allChildren);
        return nodeIds;
    }, [treeDescription]);
    // Initialize our state using the correct "expandedByDefault" state
    var initGraphStore = useGraphRenderingStore(function (s) { return s.init; });
    useMemo(function () {
        var expandedSubtrees = {};
        visitTreeNodes(treeDescription.root, function (n) {
            if (n.expandedByDefault) {
                expandedSubtrees[nodeIdMapping.get(n)] = true;
            }
        }, allChildren);
        initGraphStore(expandedSubtrees);
    }, [treeDescription, initGraphStore, nodeIdMapping]);
    // Create a ResizeObserver to keep track of the sizes of the nodes
    var resizeObserverRef = useRef();
    var updateNodeDimensions = useGraphRenderingStore(function (s) { return s.updateNodeDimensions; });
    var resizeObserver = useMemo(function () {
        var _a;
        (_a = resizeObserverRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        var observer = new ResizeObserver(updateNodeDimensions);
        resizeObserverRef.current = observer;
        return observer;
    }, [updateNodeDimensions]);
    useEffect(function () {
        return function () {
            var _a;
            (_a = resizeObserverRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
        };
    }, []);
    // Layout the tree, using the actual measured sizes of the DOM nodes
    var nodeDimensions = useGraphRenderingStore(function (s) { return s.nodeDimensions; });
    var expandedNodes = useGraphRenderingStore(function (s) { return s.expandedNodes; });
    var expandedSubtrees = useGraphRenderingStore(function (s) { return s.expandedSubtrees; });
    var layout = useMemo(function () { return layoutTree(treeDescription, nodeIdMapping, nodeDimensions, expandedNodes, expandedSubtrees, resizeObserver); }, [treeDescription, nodeIdMapping, nodeDimensions, expandedNodes, expandedSubtrees, resizeObserver]);
    return (_jsxs(ReactFlow, { nodes: layout.nodes, edges: layout.edges, nodeOrigin: [0.5, 0], nodeTypes: nodeTypes, fitView: true, minZoom: 0.2, maxZoom: 1.5, elementsSelectable: true, nodesDraggable: false, nodesConnectable: false, nodesFocusable: false, className: "query-graph", children: __spreadArray(__spreadArray([], __read(Array.isArray(children) ? children : [children]), false), [_jsx(MiniMap, { zoomable: true, pannable: true, nodeColor: minimapNodeColor }), _jsx(Controls, { showInteractive: false })], false) }));
}
export function QueryGraph(props) {
    return (_jsx(ReactFlowProvider, { children: _jsx(QueryGraphInternal, __assign({}, props)) }));
}
